import { forwardRef, useState } from 'react';
import { cn } from '../tokens/cn';
import type { VariantProps } from '../tokens/variants';
import {
  avatarVariants,
  initialsFromName,
  pickGradient,
} from './avatar.variants';

export interface AvatarProps
  extends Omit<React.HTMLAttributes<HTMLSpanElement>, 'children'>,
    VariantProps<typeof avatarVariants> {
  /** Display name. Used to derive initials when no `src` is provided. */
  name?: string;
  /** Optional image URL. If it errors, the component falls back to initials. */
  src?: string;
  /** Override the seed used to pick the gradient (default: `name`). Useful so
   *  the same agent / contact always gets the same color across rebrands. */
  seed?: string;
  /** Override the initials (e.g. for system avatars or one-letter slugs). */
  initials?: string;
  /** Optional small badge anchored at the bottom-right (e.g. channel chip,
   *  status dot). Pass any node — usually a 14px-16px rounded badge. */
  badge?: React.ReactNode;
}

/**
 * Generic avatar primitive. Uses an `<img>` when `src` is given (with a
 * silent fallback to initials on load error), otherwise renders the initials
 * over a stable per-seed gradient.
 */
export const Avatar = forwardRef<HTMLSpanElement, AvatarProps>(
  (
    { name, src, seed, initials, size, shape, badge, className, ...rest },
    ref,
  ) => {
    const [imgFailed, setImgFailed] = useState(false);
    const safeName = name ?? '';
    const finalInitials =
      initials ?? (safeName ? initialsFromName(safeName) : '?');
    const showImg = !!src && !imgFailed;
    const background = pickGradient(seed ?? safeName ?? finalInitials);

    const clipShape =
      shape === 'rounded' ? 'rounded-tk-md' : 'rounded-tk-full';

    return (
      <span
        ref={ref}
        className={cn(avatarVariants({ size, shape }), className)}
        aria-hidden={!name && !initials}
        {...rest}
      >
        <span
          className={cn(
            'absolute inset-0 inline-flex items-center justify-center overflow-hidden',
            clipShape,
          )}
          style={!showImg ? { background } : undefined}
        >
          {showImg ? (
            <img
              src={src}
              alt={name ?? ''}
              className="h-full w-full object-cover"
              onError={() => setImgFailed(true)}
            />
          ) : (
            <span>{finalInitials}</span>
          )}
        </span>
        {badge && (
          <span
            aria-hidden
            className="absolute -bottom-0.5 -right-0.5 z-10 inline-flex items-center justify-center"
          >
            {badge}
          </span>
        )}
      </span>
    );
  },
);

Avatar.displayName = 'Avatar';
